import { DEFAULT_SETTINGS } from "../data/seedData.js";
import { createNoSessionSupabaseClient, supabase, hasSupabaseConfig } from "./supabaseClient.js";
import { DEFAULT_FILE_BUCKET, getFileUrl, uploadCompressedImage } from "./fileUpload.js";

const STORAGE_KEY = "staffpay-attendance-payroll-v2";

export function createInitialState() {
  return {
    settings: DEFAULT_SETTINGS,
    stores: [],
    employees: [],
    users: [
      {
        id: "user-super-admin",
        name: "Super Admin",
        email: "admin@company.local",
        role: "super_admin",
        employeeId: "",
        storeIds: [],
        permissions: { all: true },
        active: true
      }
    ],
    currentUserId: "user-super-admin",
    salaryHistory: [],
    storeHistory: [],
    weeklyOffHistory: [],
    shiftHistory: [],
    geofenceHistory: [],
    payrollRuleHistory: [],
    incentiveRuleHistory: [],
    punches: [],
    attendanceOverrides: [],
    corrections: [],
    leaveBalances: [],
    advancesLoans: [],
    reimbursements: [],
    storeCollections: [],
    incentiveRecords: [],
    payrollRuns: [],
    salarySlips: [],
    approvals: [],
    workTasks: [],
    salesTargets: [],
    opticalCustomers: [],
    opticalFastSlips: [],
    opticalDailyRegisterEntries: [],
    opticalPendingPaymentEntries: [],
    opticalDeliveryTrackingEntries: [],
    opticalLensOrderEntries: [],
    crmProducts: [],
    crmStockMovements: [],
    crmPurchases: [],
    crmSuppliers: [],
    crmProductCategories: [],
    crmBrands: [],
    crmGstHsnMaster: [],
    crmInventoryStock: [],
    crmStockTransfers: [],
    crmStockAdjustments: [],
    crmVendorOrderSlips: [],
    crmVendorDcInward: [],
    crmPurchaseInvoices: [],
    crmVendorBillAudits: [],
    crmDocumentExtractions: [],
    crmUnmappedSoldItems: [],
    crmInventoryImports: [],
    crmPurchaseImports: [],
    crmCommunications: [],
    crmPrescriptions: [],
    crmAppointments: [],
    bankReco: [],
    cnRequests: [],
    importBatches: [],
    notifications: [],
    companyLedgerEntries: [],
    cnDocumentExtractions: [],
    appFiles: [],
    stockItems: [],
    auditSessions: [],
    auditSessionItems: [],
    auditPhotos: [],
    auditReviews: [],
    auditSettings: [],
    auditActivityLogs: [],
    auditLogs: []
  };
}

function readLocalState() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return saved ? { ...createInitialState(), ...saved } : createInitialState();
  } catch {
    return createInitialState();
  }
}

function writeLocalState(state) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

function uuid(prefix) {
  if (hasSupabaseConfig && crypto.randomUUID) return crypto.randomUUID();
  return `${prefix}-${crypto.randomUUID ? crypto.randomUUID() : Date.now()}`;
}

function toSnakeRecord(record) {
  const map = {
    employeeId: "employee_id",
    employeeCode: "employee_code",
    fullName: "full_name",
    storeId: "store_id",
    monthlySalary: "monthly_salary",
    weeklyOffDay: "weekly_off_day",
    reportingManagerId: "reporting_manager_id",
    joiningDate: "joining_date",
    exitDate: "exit_date",
    endDate: "end_date",
    openingLeaveBalance: "opening_leave_balance",
    openingLoanBalance: "opening_loan_balance",
    incentiveEligible: "incentive_eligible",
    reimbursementEligible: "reimbursement_eligible",
    bankDetails: "bank_details",
    radiusMeters: "radius_meters",
    geofenceEnabled: "geofence_enabled",
    effectiveDate: "effective_date",
    changedBy: "changed_by",
    payrollMonth: "payroll_month",
    deductedAmount: "deducted_amount",
    createdAt: "created_at",
    updatedAt: "updated_at",
    oldValue: "old_value",
    newValue: "new_value",
    entityType: "entity_type",
    entityId: "entity_id",
    storeIds: "store_ids",
    authUserId: "auth_user_id",
    startTime: "start_time",
    endTime: "end_time",
    fullDayOutBy: "full_day_out_by",
    halfDayCutoff: "half_day_cutoff",
    graceMinutes: "grace_minutes",
    splitPercent: "split_percent",
    payDelayMonths: "pay_delay_months",
    gpsAccuracy: "gps_accuracy",
    distanceMeters: "distance_meters",
    insideGeofence: "inside_geofence",
    exceptionRequired: "exception_required",
    photoPath: "photo_path",
    deviceInfo: "device_info",
    requestedValue: "requested_value",
    requestedBy: "requested_by",
    decidedAt: "decided_at",
    addedBy: "added_by",
    openingBalance: "opening_balance",
    closingBalance: "closing_balance",
    attachmentPath: "attachment_path",
    billPath: "bill_path",
    approvedBy: "approved_by",
    lockReason: "lock_reason",
    lockedBy: "locked_by",
    lockedAt: "locked_at",
    assignedTo: "assigned_to",
    assignedBy: "assigned_by",
    dueDate: "due_date",
    completedAt: "completed_at",
    completedBy: "completed_by",
    reviewStatus: "review_status",
    reviewedBy: "reviewed_by",
    reviewedAt: "reviewed_at",
    targetAmount: "target_amount",
    achievedAmount: "achieved_amount",
    orderNumber: "order_number",
    gcNumber: "gc_number",
    storeName: "store_name",
    storePhone: "store_phone",
    salespersonId: "salesperson_id",
    salespersonName: "salesperson_name",
    salespersonDesignation: "salesperson_designation",
    customerId: "customer_id",
    deliveryDate: "delivery_date",
    orderDetails: "order_details",
    generatedViews: "generated_views",
    slipId: "slip_id",
    customerName: "customer_name",
    noPhone: "no_phone",
    walkInId: "walk_in_id",
    totalAmount: "total_amount",
    subtotalAmount: "subtotal_amount",
    discountAmount: "discount_amount",
    discountType: "discount_type",
    discountValue: "discount_value",
    advanceReceived: "advance_received",
    paymentMode: "payment_mode",
    deliveryStatus: "delivery_status",
    lensDetails: "lens_details",
    contactLensDetails: "contact_lens_details",
    vendorOrderNumber: "vendor_order_number"
    ,
    productCode: "product_code",
    productType: "product_type",
    productName: "product_name",
    categoryId: "category_id",
    categoryName: "category_name",
    categoryCode: "category_code",
    supplierId: "supplier_id",
    supplierName: "supplier_name",
    supplierType: "supplier_type",
    contactPerson: "contact_person",
    paymentTerms: "payment_terms",
    creditCycle: "credit_cycle",
    defaultHsn: "default_hsn",
    defaultGstRate: "default_gst_rate",
    gstRate: "gst_rate",
    cgstRate: "cgst_rate",
    sgstRate: "sgst_rate",
    igstRate: "igst_rate",
    hsnCode: "hsn_code",
    sacCode: "sac_code",
    effectiveFrom: "effective_from",
    barcodeRequired: "barcode_required",
    batchRequired: "batch_required",
    expiryRequired: "expiry_required",
    powerRequired: "power_required",
    brandId: "brand_id",
    brandName: "brand_name",
    costPrice: "cost_price",
    sellingPrice: "selling_price",
    purchasePrice: "purchase_price",
    salePrice: "sale_price",
    stockQty: "stock_qty",
    reorderLevel: "reorder_level",
    barcode: "barcode",
    batchNo: "batch_no",
    expiryDate: "expiry_date",
    reservedQty: "reserved_qty",
    availableQty: "available_qty",
    purchaseSource: "purchase_source",
    movementType: "movement_type",
    fromStoreId: "from_store_id",
    toStoreId: "to_store_id",
    referenceType: "reference_type",
    referenceId: "reference_id",
    documentType: "document_type",
    documentNo: "document_no",
    documentDate: "document_date",
    gstin: "gstin",
    supplierGstin: "supplier_gstin",
    supplierPaymentTerms: "supplier_payment_terms",
    taxRule: "tax_rule",
    taxType: "tax_type",
    inputCreditAmount: "input_credit_amount",
    creditorBalance: "creditor_balance",
    invoiceId: "invoice_id",
    entryDate: "entry_date",
    entryType: "entry_type",
    debitAmount: "debit_amount",
    creditAmount: "credit_amount",
    balanceAmount: "balance_amount",
    unitPrice: "unit_price",
    basePrice: "base_price",
    totalPurchasePrice: "total_purchase_price",
    retailPrice: "retail_price",
    dcNumber: "dc_number",
    transferDate: "transfer_date",
    lineItems: "line_items",
    taxableValue: "taxable_value",
    taxAmount: "tax_amount",
    confidenceLevel: "confidence_level",
    extractionStatus: "extraction_status",
    extractedFields: "extracted_fields",
    confirmedFields: "confirmed_fields",
    proofFilePath: "proof_file_path",
    linkedOrderId: "linked_order_id",
    linkedOrderItemId: "linked_order_item_id",
    vendorOrderSlipId: "vendor_order_slip_id",
    vendorDcId: "vendor_dc_id",
    vendorDcItemId: "vendor_dc_item_id",
    purchaseInvoiceId: "purchase_invoice_id",
    auditStatus: "audit_status",
    mismatchSummary: "mismatch_summary",
    invoiceNumber: "invoice_number",
    vendorName: "vendor_name",
    billDate: "bill_date",
    paidAmount: "paid_amount",
    customerPhone: "customer_phone",
    followUpDate: "follow_up_date",
    messageType: "message_type",
    linkedOrderId: "linked_order_id",
    rightEye: "right_eye",
    leftEye: "left_eye",
    appointmentDate: "appointment_date",
    appointmentTime: "appointment_time",
    recoDate: "reco_date",
    cashInHand: "cash_in_hand",
    bankCredit: "bank_credit",
    posMachineSettlement: "pos_machine_settlement",
    upiReceived: "upi_received",
    cardReceived: "card_received",
    enteredBy: "entered_by",
    markedDoneBy: "marked_done_by"
    ,
    orderItemId: "order_item_id",
    enteredProductName: "entered_product_name",
    categoryGuessed: "category_guessed",
    productMatchStatus: "product_match_status",
    inventoryStatus: "inventory_status",
    actionNeeded: "action_needed",
    resolutionAction: "resolution_action",
    resolvedProductName: "resolved_product_name",
    resolvedBy: "resolved_by",
    resolvedAt: "resolved_at",
    importType: "import_type",
    previewRows: "preview_rows",
    mappedRows: "mapped_rows",
    mapping: "mapping",
    summary: "summary",
    confirmedAt: "confirmed_at",
    confirmedBy: "confirmed_by",
    errorReport: "error_report",
    negativeInventory: "negative_inventory",
    previousOrderNo: "previous_order_no",
    newOrderNo: "new_order_no",
    previousOrderDate: "previous_order_date",
    newOrderDate: "new_order_date",
    productDetails: "product_details",
    productType: "product_type",
    reasonForReturn: "reason_for_return",
    otherReasonText: "other_reason_text",
    complaintNote: "complaint_note",
    cnTicketNumber: "cn_ticket_number",
    cnaNumber: "cna_number",
    importBatchId: "import_batch_id",
    source: "source",
    importedSheetName: "imported_sheet_name",
    importedRowNumber: "imported_row_number",
    importedStatusText: "imported_status_text",
    importedColorCode: "imported_color_code",
    createdByImportUser: "created_by_import_user",
    importedAt: "imported_at",
    orderReferencePhoto: "order_reference_photo",
    supportingPhoto: "supporting_photo",
    cnFormPhoto: "cn_form_photo",
    fastSlipPhoto: "fast_slip_photo",
    oldItemPhoto: "old_item_photo",
    createdBy: "created_by",
    createdByName: "created_by_name",
    createdDate: "created_date",
    packetChecklist: "packet_checklist",
    missingItemReason: "missing_item_reason",
    vendorName: "vendor_name",
    vendorReferenceNo: "vendor_reference_no",
    vendorDocumentReference: "vendor_document_reference",
    productNameAsPerDc: "product_name_as_per_dc",
    dcNumber: "dc_number",
    companyFormNo: "company_form_no",
    dispatchDate: "dispatch_date",
    pickupDate: "pickup_date",
    pickedBy: "picked_by",
    readyForDispatchDate: "ready_for_dispatch_date",
    collectedBy: "collected_by",
    sentThrough: "sent_through",
    amountExclusiveTax: "amount_exclusive_tax",
    expectedCnAmountExTax: "expected_cn_amount_ex_tax",
    expectedCnAmountIncTax: "expected_cn_amount_inc_tax",
    vendorFormPhoto: "vendor_form_photo",
    cnCopyPhoto: "cn_copy_photo",
    cnNumber: "cn_number",
    cnDate: "cn_date",
    cnReceivedDate: "cn_received_date",
    cnAmount: "cn_amount",
    settlementAmount: "settlement_amount",
    differenceAmount: "difference_amount",
    draftCnNumber: "draft_cn_number",
    draftCnDate: "draft_cn_date",
    draftCnAmount: "draft_cn_amount",
    draftSettlementAmount: "draft_settlement_amount",
    draftDifferenceAmount: "draft_difference_amount",
    needsReview: "needs_review",
    ledgerUpdated: "ledger_updated",
    closedAt: "closed_at",
    closedBy: "closed_by",
    closedByName: "closed_by_name",
    closureRemarks: "closure_remarks",
    closureOutcome: "closure_outcome",
    lastFollowUpNote: "last_follow_up_note",
    lastFollowUpAt: "last_follow_up_at",
    reappliedAt: "reapplied_at",
    cnResponsibleId: "cn_responsible_id",
    cnResponsibleIds: "cn_responsible_ids",
    cnActivity: "cn_activity",
    statusCorrectedAt: "status_corrected_at",
    statusCorrectedBy: "status_corrected_by",
    statusCorrectionReason: "status_correction_reason",
    statusCorrections: "status_corrections",
    vendorAssignedAt: "vendor_assigned_at",
    packetPrintedAt: "packet_printed_at",
    cnCaseId: "cn_case_id",
    fileId: "file_id",
    documentType: "document_type",
    extractedText: "extracted_text",
    extractedFieldsJson: "extracted_fields_json",
    confidenceScore: "confidence_score",
    extractionStatus: "extraction_status",
    extractedAt: "extracted_at",
    verifiedBy: "verified_by",
    verifiedAt: "verified_at",
    finalCnNumber: "final_cn_number",
    finalCnDate: "final_cn_date",
    finalCnAmount: "final_cn_amount",
    finalVendorReference: "final_vendor_reference",
    totalRows: "total_rows",
    importedRows: "imported_rows",
    duplicateRows: "duplicate_rows",
    failedRows: "failed_rows",
    needsReviewRows: "needs_review_rows",
    fileName: "file_name",
    uploadedBy: "uploaded_by",
    uploadedAt: "uploaded_at",
    userId: "user_id",
    linkedRecordType: "linked_record_type",
    linkedRecordId: "linked_record_id",
    entityType: "entity_type",
    entityId: "entity_id",
    readAt: "read_at",
    createdBy: "created_by",
    isRead: "is_read",
    recordType: "record_type",
    recordId: "record_id",
    oldStatus: "old_status",
    newStatus: "new_status",
    changedBy: "changed_by",
    changedByName: "changed_by_name",
    staffId: "staff_id",
    fileType: "file_type",
    storageProvider: "storage_provider",
    storageBucket: "storage_bucket",
    storagePath: "storage_path",
    originalFileName: "original_file_name",
    mimeType: "mime_type",
    originalFileSizeBytes: "original_file_size_bytes",
    compressedFileSizeBytes: "compressed_file_size_bytes",
    imageWidth: "image_width",
    imageHeight: "image_height",
    uploadedBy: "uploaded_by",
    uploadedAt: "uploaded_at",
    isDeleted: "is_deleted",
    deletedBy: "deleted_by",
    deletedAt: "deleted_at",
    currentStoreId: "current_store_id",
    articleName: "article_name",
    lastAuditedAt: "last_audited_at",
    lastAuditStatus: "last_audit_status",
    auditType: "audit_type",
    targetCount: "target_count",
    deadlineAt: "deadline_at",
    mixAdjustmentNote: "mix_adjustment_note",
    auditSessionId: "audit_session_id",
    stockItemId: "stock_item_id",
    expectedStoreId: "expected_store_id",
    selectionBucket: "selection_bucket",
    verificationStatus: "verification_status",
    scannedBarcode: "scanned_barcode",
    verifiedBy: "verified_by",
    verifiedAt: "verified_at",
    auditSessionItemId: "audit_session_item_id",
    photoUrl: "photo_url",
    reviewAction: "review_action",
    reviewNote: "review_note"
  };
  const emptyStringMeansNull = new Set([
    "store_id",
    "employee_id",
    "reporting_manager_id",
    "salesperson_id",
    "customer_id",
    "slip_id",
    "product_id",
    "supplier_id",
    "from_store_id",
    "to_store_id",
    "entered_by",
    "marked_done_by",
    "created_by",
    "requested_by",
    "approved_by",
    "added_by",
    "assigned_to",
    "assigned_by",
    "completed_by",
    "reviewed_by",
    "verified_by",
    "stock_item_id",
    "audit_session_id",
    "audit_session_item_id",
    "expected_store_id",
    "cn_responsible_id",
    "cn_case_id",
    "file_id",
    "uploaded_by",
    "deleted_by",
    "verified_by"
  ]);
  return Object.fromEntries(
    Object.entries(record)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => {
        const snakeKey = map[key] || key;
        return [snakeKey, emptyStringMeansNull.has(snakeKey) && value === "" ? null : value];
      })
  );
}

function toCamelRecord(record) {
  if (!record) return record;
  return Object.fromEntries(
    Object.entries(record).map(([key, value]) => [
      key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase()),
      value
    ])
  );
}

async function optionalTable(builder) {
  const result = await builder;
  const message = String(result.error?.message || "").toLowerCase();
  if (result.error && (message.includes("does not exist") || message.includes("schema cache"))) {
    return { data: [], error: null };
  }
  return result;
}

const CN_REQUEST_SAFE_COLUMNS = new Set([
  "id",
  "cn_ticket_number",
  "cna_number",
  "store_id",
  "previous_order_no",
  "new_order_no",
  "product_details",
  "product_type",
  "quantity",
  "reason_for_return",
  "other_reason_text",
  "complaint_note",
  "order_reference_photo",
  "supporting_photo",
  "old_item_photo",
  "created_by",
  "created_by_name",
  "created_date",
  "cn_responsible_id",
  "cn_responsible_ids",
  "status",
  "packet_checklist",
  "missing_item_reason",
  "vendor_name",
  "vendor_reference_no",
  "vendor_document_reference",
  "product_name_as_per_dc",
  "dc_number",
  "company_form_no",
  "dispatch_date",
  "pickup_date",
  "picked_by",
  "collected_by",
  "sent_through",
  "amount_exclusive_tax",
  "expected_cn_amount_ex_tax",
  "expected_cn_amount_inc_tax",
  "vendor_form_photo",
  "cn_copy_photo",
  "cn_number",
  "cn_date",
  "cn_received_date",
  "cn_amount",
  "settlement_amount",
  "difference_amount",
  "draft_cn_number",
  "draft_cn_date",
  "draft_cn_amount",
  "draft_settlement_amount",
  "draft_difference_amount",
  "remarks",
  "ledger_updated",
  "closed_at",
  "closed_by",
  "closed_by_name",
  "closure_remarks",
  "closure_outcome",
  "last_follow_up_note",
  "last_follow_up_at",
  "reapplied_at",
  "cn_activity",
  "vendor_assigned_at",
  "packet_printed_at",
  "source",
  "import_batch_id",
  "imported_sheet_name",
  "imported_row_number",
  "imported_status_text",
  "imported_color_code",
  "created_by_import_user",
  "imported_at",
  "needs_review",
  "status_corrected_at",
  "status_corrected_by",
  "status_correction_reason",
  "status_corrections",
  "created_at",
  "updated_at"
]);

const TABLE_SAFE_COLUMNS = {
  cn_requests: CN_REQUEST_SAFE_COLUMNS,
  optical_customers: new Set([
    "id",
    "phone",
    "no_phone",
    "walk_in_id",
    "name",
    "address",
    "dob",
    "email",
    "store_id",
    "created_at",
    "updated_at"
  ]),
  optical_fast_slips: new Set([
    "id",
    "order_number",
    "gc_number",
    "store_id",
    "store_name",
    "store_phone",
    "salesperson_id",
    "salesperson_name",
    "salesperson_designation",
    "date",
    "delivery_date",
    "customer_id",
    "customer",
    "order_details",
    "power",
    "payment",
    "generated_views",
    "created_at",
    "updated_at"
  ]),
  optical_daily_register_entries: new Set([
    "id",
    "slip_id",
    "order_number",
    "date",
    "customer_name",
    "phone",
    "subtotal_amount",
    "discount_amount",
    "total_amount",
    "advance_received",
    "balance",
    "payment_mode",
    "delivery_date",
    "delivery_status",
    "store_id",
    "salesperson_id",
    "created_at",
    "updated_at"
  ]),
  optical_pending_payment_entries: new Set([
    "id",
    "slip_id",
    "order_number",
    "customer_name",
    "phone",
    "balance",
    "subtotal_amount",
    "discount_amount",
    "total_amount",
    "advance_received",
    "status",
    "store_id",
    "salesperson_id",
    "created_at",
    "updated_at"
  ]),
  optical_delivery_tracking_entries: new Set([
    "id",
    "slip_id",
    "order_number",
    "customer_name",
    "delivery_date",
    "status",
    "store_id",
    "salesperson_id",
    "created_at",
    "updated_at"
  ]),
  optical_lens_order_entries: new Set([
    "id",
    "slip_id",
    "order_number",
    "customer_name",
    "lens_details",
    "contact_lens_details",
    "power",
    "vendor",
    "vendor_order_number",
    "status",
    "store_id",
    "salesperson_id",
    "created_at",
    "updated_at"
  ]),
  users: new Set([
    "id",
    "auth_user_id",
    "name",
    "email",
    "role",
    "employee_id",
    "store_ids",
    "permissions",
    "active",
    "created_at",
    "updated_at"
  ]),
  stores: new Set([
    "id",
    "name",
    "code",
    "address",
    "latitude",
    "longitude",
    "radius_meters",
    "geofence_enabled",
    "phone",
    "active",
    "created_at",
    "updated_at"
  ]),
  employees: new Set([
    "id",
    "employee_code",
    "full_name",
    "phone",
    "email",
    "store_id",
    "designation",
    "reporting_manager_id",
    "monthly_salary",
    "weekly_off_day",
    "joining_date",
    "exit_date",
    "opening_leave_balance",
    "opening_loan_balance",
    "incentive_eligible",
    "reimbursement_eligible",
    "bank_details",
    "notes",
    "active",
    "created_at",
    "updated_at"
  ]),
  salary_history: new Set([
    "id",
    "employee_id",
    "monthly_salary",
    "effective_date",
    "reason",
    "changed_by",
    "created_at",
    "updated_at"
  ]),
  employee_store_history: new Set([
    "id",
    "employee_id",
    "store_id",
    "effective_date",
    "reason",
    "changed_by",
    "created_at",
    "updated_at"
  ]),
  weekly_off_history: new Set([
    "id",
    "employee_id",
    "weekly_off_day",
    "effective_date",
    "reason",
    "changed_by",
    "created_at",
    "updated_at"
  ]),
  shift_history: new Set([
    "id",
    "employee_id",
    "start_time",
    "end_time",
    "full_day_out_by",
    "half_day_cutoff",
    "grace_minutes",
    "effective_date",
    "reason",
    "changed_by",
    "created_at",
    "updated_at"
  ]),
  geofence_history: new Set([
    "id",
    "store_id",
    "latitude",
    "longitude",
    "radius_meters",
    "geofence_enabled",
    "effective_date",
    "reason",
    "changed_by",
    "created_at",
    "updated_at"
  ]),
  incentive_rule_history: new Set([
    "id",
    "employee_id",
    "store_id",
    "threshold",
    "rate",
    "split_percent",
    "pay_delay_months",
    "active",
    "effective_date",
    "reason",
    "changed_by",
    "created_at",
    "updated_at"
  ]),
  attendance_punches: new Set([
    "id",
    "employee_id",
    "store_id",
    "date",
    "type",
    "timestamp",
    "latitude",
    "longitude",
    "gps_accuracy",
    "distance_meters",
    "inside_geofence",
    "exception_required",
    "photo_path",
    "device_info",
    "created_at",
    "updated_at"
  ]),
  attendance_corrections: new Set([
    "id",
    "employee_id",
    "date",
    "old_value",
    "requested_value",
    "requested_by",
    "approved_by",
    "reason",
    "status",
    "created_at",
    "updated_at",
    "decided_at"
  ]),
  advances_loans: new Set([
    "id",
    "employee_id",
    "date",
    "amount",
    "type",
    "payroll_month",
    "notes",
    "added_by",
    "approved_by",
    "status",
    "opening_balance",
    "deducted_amount",
    "closing_balance",
    "attachment_path",
    "created_at",
    "updated_at"
  ]),
  reimbursements: new Set([
    "id",
    "employee_id",
    "date",
    "category",
    "amount",
    "description",
    "payroll_month",
    "notes",
    "approved_by",
    "status",
    "bill_path",
    "created_at",
    "updated_at"
  ]),
  work_tasks: new Set([
    "id",
    "title",
    "description",
    "assigned_to",
    "assigned_by",
    "due_date",
    "priority",
    "status",
    "completed_at",
    "completed_by",
    "review_status",
    "reviewed_by",
    "reviewed_at",
    "created_at",
    "updated_at"
  ]),
  sales_targets: new Set([
    "id",
    "employee_id",
    "store_id",
    "month",
    "target_amount",
    "achieved_amount",
    "notes",
    "assigned_by",
    "created_at",
    "updated_at"
  ]),
  app_files: new Set([
    "id",
    "module",
    "record_type",
    "record_id",
    "store_id",
    "staff_id",
    "file_type",
    "storage_provider",
    "storage_bucket",
    "storage_path",
    "original_file_name",
    "mime_type",
    "original_file_size_bytes",
    "compressed_file_size_bytes",
    "image_width",
    "image_height",
    "uploaded_by",
    "uploaded_at",
    "is_deleted",
    "deleted_by",
    "deleted_at",
    "created_at",
    "updated_at"
  ]),
  bank_reco: new Set([
    "id",
    "store_id",
    "reco_date",
    "cash_in_hand",
    "bank_credit",
    "pos_machine_settlement",
    "upi_received",
    "card_received",
    "notes",
    "status",
    "entered_by",
    "marked_done_by",
    "created_at",
    "updated_at"
  ]),
  stock_items: new Set([
    "id",
    "store_id",
    "current_store_id",
    "barcode",
    "product_code",
    "product_name",
    "product_type",
    "category_name",
    "brand_name",
    "article_name",
    "batch_no",
    "expiry_date",
    "stock_qty",
    "reserved_qty",
    "available_qty",
    "cost_price",
    "selling_price",
    "purchase_price",
    "retail_price",
    "status",
    "last_audited_at",
    "last_audit_status",
    "created_at",
    "updated_at"
  ]),
  crm_unmapped_sold_items: new Set([
    "id",
    "order_id",
    "order_item_id",
    "order_number",
    "store_id",
    "salesperson_id",
    "salesperson_name",
    "entered_product_name",
    "barcode",
    "category_guessed",
    "quantity",
    "sale_price",
    "product_match_status",
    "inventory_status",
    "action_needed",
    "resolution_action",
    "resolved_product_name",
    "resolved_by",
    "resolved_at",
    "status",
    "created_by",
    "created_at",
    "updated_at"
  ]),
  crm_stock_movements: new Set([
    "id",
    "product_id",
    "order_id",
    "order_item_id",
    "order_number",
    "product_name",
    "entered_product_name",
    "store_id",
    "from_store_id",
    "to_store_id",
    "movement_type",
    "quantity",
    "reference_type",
    "reference_id",
    "negative_inventory",
    "action_needed",
    "remarks",
    "created_by",
    "created_at",
    "updated_at"
  ]),
  status_change_logs: new Set([
    "id",
    "module",
    "record_type",
    "record_id",
    "old_status",
    "new_status",
    "changed_by",
    "changed_by_name",
    "reason",
    "created_at"
  ]),
  notifications: new Set([
    "id",
    "user_id",
    "title",
    "message",
    "type",
    "priority",
    "entity_type",
    "entity_id",
    "store_id",
    "is_read",
    "read_at",
    "created_by",
    "created_at",
    "updated_at",
    "module",
    "linked_record_type",
    "linked_record_id"
  ]),
  app_settings: new Set(["id", "settings", "created_at", "updated_at"]),
  audit_logs: new Set([
    "id",
    "entity_type",
    "entity_id",
    "action",
    "old_value",
    "new_value",
    "reason",
    "effective_date",
    "changed_by",
    "created_at"
  ])
};

function schemaGuardWarning(tableName, unknownKeys) {
  if (!unknownKeys.length) return;
  console.warn(`Schema guard removed UI-only/unknown fields before saving ${tableName}:`, unknownKeys);
}

function buildSupabasePayload(tableName, record) {
  const snakeRecord = toSnakeRecord(record || {});
  const allowedColumns = TABLE_SAFE_COLUMNS[tableName];
  if (!allowedColumns) return snakeRecord;
  const unknownKeys = Object.keys(snakeRecord).filter((key) => !allowedColumns.has(key));
  schemaGuardWarning(tableName, unknownKeys);
  return Object.fromEntries(Object.entries(snakeRecord).filter(([key]) => allowedColumns.has(key)));
}

function extractUnknownSchemaColumn(error) {
  const message = `${error?.message || ""} ${error?.details || ""} ${error?.hint || ""}`;
  const match = message.match(/Could not find the '([^']+)' column/i);
  return match?.[1] || "";
}

async function upsertSchemaSafe(tableName, record) {
  const droppedColumns = [];
  const payload = buildSupabasePayload(tableName, record);
  let workingPayload = { ...payload };
  for (let attempt = 0; attempt < 8; attempt += 1) {
    const { data, error } = await supabase.from(tableName).upsert(workingPayload).select().single();
    if (!error) {
      if (droppedColumns.length) {
        console.warn(`Saved ${tableName} after removing schema-mismatched fields:`, droppedColumns);
      }
      return data;
    }
    const unknownColumn = extractUnknownSchemaColumn(error);
    if (unknownColumn && Object.prototype.hasOwnProperty.call(workingPayload, unknownColumn)) {
      droppedColumns.push(unknownColumn);
      delete workingPayload[unknownColumn];
      console.error(`Supabase rejected unknown ${tableName}.${unknownColumn}; retrying without that field.`, error);
      continue;
    }
    throw error;
  }
  throw new Error(`Too many schema mismatches while saving ${tableName}: ${droppedColumns.join(", ")}`);
}

async function persistCnRequestCompat(record) {
  const safeRecord = buildSupabasePayload("cn_requests", record);
  if (!safeRecord.id) throw new Error("CN case id is missing.");
  const data = await upsertSchemaSafe("cn_requests", safeRecord);
  return toCamelRecord(data);
}

async function loadSupabaseData() {
  const auth = await supabase.auth.getUser();
  if (auth.data.user?.email) {
    try {
      await supabase.rpc("claim_user_profile_by_email");
    } catch {
      // The app can still load if the helper function has not been installed yet.
    }
  }
  const [
    stores,
    employees,
    users,
    salaryHistory,
    storeHistory,
    weeklyOffHistory,
    shiftHistory,
    geofenceHistory,
    incentiveRuleHistory,
    punches,
    corrections,
    leaveBalances,
    advancesLoans,
    reimbursements,
    storeCollections,
    incentiveRecords,
    payrollRuns,
    workTasks,
    salesTargets,
    opticalCustomers,
    opticalFastSlips,
    opticalDailyRegisterEntries,
    opticalPendingPaymentEntries,
    opticalDeliveryTrackingEntries,
    opticalLensOrderEntries,
    crmProducts,
    crmStockMovements,
    crmPurchases,
    crmSuppliers,
    crmProductCategories,
    crmBrands,
    crmGstHsnMaster,
    crmInventoryStock,
    crmStockTransfers,
    crmStockAdjustments,
    crmVendorOrderSlips,
    crmVendorDcInward,
    crmPurchaseInvoices,
    crmVendorBillAudits,
    crmDocumentExtractions,
    crmUnmappedSoldItems,
    crmInventoryImports,
    crmPurchaseImports,
    crmCommunications,
    crmPrescriptions,
    crmAppointments,
    bankReco,
    cnRequests,
    importBatches,
    notifications,
    companyLedgerEntries,
    cnDocumentExtractions,
    appFiles,
    stockItems,
    auditSessions,
    auditSessionItems,
    auditPhotos,
    auditReviews,
    auditSettings,
    auditActivityLogs,
    auditLogs,
    appSettings
  ] = await Promise.all([
    supabase.from("stores").select("*").order("name"),
    supabase.from("employees").select("*").order("employee_code"),
    supabase.from("users").select("*"),
    supabase.from("salary_history").select("*"),
    supabase.from("employee_store_history").select("*"),
    supabase.from("weekly_off_history").select("*"),
    supabase.from("shift_history").select("*"),
    supabase.from("geofence_history").select("*"),
    supabase.from("incentive_rule_history").select("*"),
    supabase.from("attendance_punches").select("*"),
    supabase.from("attendance_corrections").select("*"),
    supabase.from("leave_balances").select("*"),
    supabase.from("advances_loans").select("*"),
    supabase.from("reimbursements").select("*"),
    supabase.from("store_collections").select("*"),
    supabase.from("incentive_records").select("*"),
    supabase.from("payroll_runs").select("*"),
    optionalTable(supabase.from("work_tasks").select("*").order("created_at", { ascending: false })),
    optionalTable(supabase.from("sales_targets").select("*").order("month", { ascending: false })),
    optionalTable(supabase.from("optical_customers").select("*").order("created_at", { ascending: false })),
    optionalTable(supabase.from("optical_fast_slips").select("*").order("created_at", { ascending: false })),
    optionalTable(supabase.from("optical_daily_register_entries").select("*").order("created_at", { ascending: false })),
    optionalTable(supabase.from("optical_pending_payment_entries").select("*").order("created_at", { ascending: false })),
    optionalTable(supabase.from("optical_delivery_tracking_entries").select("*").order("created_at", { ascending: false })),
    optionalTable(supabase.from("optical_lens_order_entries").select("*").order("created_at", { ascending: false })),
    optionalTable(supabase.from("crm_products").select("*").order("created_at", { ascending: false })),
    optionalTable(supabase.from("crm_stock_movements").select("*").order("created_at", { ascending: false })),
    optionalTable(supabase.from("crm_purchases").select("*").order("created_at", { ascending: false })),
    optionalTable(supabase.from("crm_suppliers").select("*").order("supplier_name", { ascending: true })),
    optionalTable(supabase.from("crm_product_categories").select("*").order("category_name", { ascending: true })),
    optionalTable(supabase.from("crm_brands").select("*").order("brand_name", { ascending: true })),
    optionalTable(supabase.from("crm_gst_hsn_master").select("*").order("hsn_code", { ascending: true })),
    optionalTable(supabase.from("crm_inventory_stock").select("*").order("created_at", { ascending: false })),
    optionalTable(supabase.from("crm_stock_transfers").select("*").order("created_at", { ascending: false })),
    optionalTable(supabase.from("crm_stock_adjustments").select("*").order("created_at", { ascending: false })),
    optionalTable(supabase.from("crm_vendor_order_slips").select("*").order("created_at", { ascending: false })),
    optionalTable(supabase.from("crm_vendor_dc_inward").select("*").order("created_at", { ascending: false })),
    optionalTable(supabase.from("crm_purchase_invoices").select("*").order("created_at", { ascending: false })),
    optionalTable(supabase.from("crm_vendor_bill_audits").select("*").order("created_at", { ascending: false })),
    optionalTable(supabase.from("crm_document_extractions").select("*").order("created_at", { ascending: false })),
    optionalTable(supabase.from("crm_unmapped_sold_items").select("*").order("created_at", { ascending: false })),
    optionalTable(supabase.from("crm_inventory_imports").select("*").order("created_at", { ascending: false })),
    optionalTable(supabase.from("crm_purchase_imports").select("*").order("created_at", { ascending: false })),
    optionalTable(supabase.from("crm_communications").select("*").order("created_at", { ascending: false })),
    optionalTable(supabase.from("crm_prescriptions").select("*").order("created_at", { ascending: false })),
    optionalTable(supabase.from("crm_appointments").select("*").order("created_at", { ascending: false })),
    optionalTable(supabase.from("bank_reco").select("*").order("reco_date", { ascending: false })),
    optionalTable(supabase.from("cn_requests").select("*").order("created_at", { ascending: false })),
    optionalTable(supabase.from("import_batches").select("*").order("uploaded_at", { ascending: false })),
    optionalTable(supabase.from("notifications").select("*").order("created_at", { ascending: false })),
    optionalTable(supabase.from("company_ledger_entries").select("*").order("created_at", { ascending: false })),
    optionalTable(supabase.from("cn_document_extractions").select("*").order("extracted_at", { ascending: false })),
    optionalTable(supabase.from("app_files").select("*").eq("is_deleted", false).order("uploaded_at", { ascending: false })),
    optionalTable(supabase.from("stock_items").select("*").order("updated_at", { ascending: false })),
    optionalTable(supabase.from("audit_sessions").select("*").order("created_at", { ascending: false })),
    optionalTable(supabase.from("audit_session_items").select("*").order("created_at", { ascending: false })),
    optionalTable(supabase.from("audit_photos").select("*").order("uploaded_at", { ascending: false })),
    optionalTable(supabase.from("audit_reviews").select("*").order("reviewed_at", { ascending: false })),
    optionalTable(supabase.from("audit_settings").select("*").order("created_at", { ascending: false })),
    optionalTable(supabase.from("audit_activity_logs").select("*").order("created_at", { ascending: false })),
    supabase.from("audit_logs").select("*").order("created_at", { ascending: false }).limit(200),
    supabase.from("app_settings").select("*").eq("id", "global").maybeSingle()
  ]);

  const errors = [
    stores,
    employees,
    users,
    salaryHistory,
    storeHistory,
    weeklyOffHistory,
    shiftHistory,
    geofenceHistory,
    incentiveRuleHistory,
    punches,
    corrections,
    leaveBalances,
    advancesLoans,
    reimbursements,
    storeCollections,
    incentiveRecords,
    payrollRuns,
    workTasks,
    salesTargets,
    opticalCustomers,
    opticalFastSlips,
    opticalDailyRegisterEntries,
    opticalPendingPaymentEntries,
    opticalDeliveryTrackingEntries,
    opticalLensOrderEntries,
    crmProducts,
    crmStockMovements,
    crmPurchases,
    crmSuppliers,
    crmProductCategories,
    crmBrands,
    crmGstHsnMaster,
    crmInventoryStock,
    crmStockTransfers,
    crmStockAdjustments,
    crmVendorOrderSlips,
    crmVendorDcInward,
    crmPurchaseInvoices,
    crmVendorBillAudits,
    crmDocumentExtractions,
    crmUnmappedSoldItems,
    crmInventoryImports,
    crmPurchaseImports,
    crmCommunications,
    crmPrescriptions,
    crmAppointments,
    bankReco,
    cnRequests,
    importBatches,
    notifications,
    companyLedgerEntries,
    cnDocumentExtractions,
    appFiles,
    stockItems,
    auditSessions,
    auditSessionItems,
    auditPhotos,
    auditReviews,
    auditSettings,
    auditActivityLogs,
    auditLogs,
    appSettings
  ].filter((result) => result.error);
  if (errors.length) throw errors[0].error;

  return {
    settings: { ...DEFAULT_SETTINGS, ...(appSettings.data?.settings || {}) },
    stores: stores.data.map(toCamelRecord),
    employees: employees.data.map(toCamelRecord),
    users: users.data.map(toCamelRecord),
    currentUserId: users.data.map(toCamelRecord).find((item) => item.authUserId === auth.data.user?.id && item.active !== false)?.id || "",
    authEmail: auth.data.user?.email || "",
    salaryHistory: salaryHistory.data.map(toCamelRecord),
    storeHistory: storeHistory.data.map(toCamelRecord),
    weeklyOffHistory: weeklyOffHistory.data.map(toCamelRecord),
    shiftHistory: shiftHistory.data.map(toCamelRecord),
    geofenceHistory: geofenceHistory.data.map(toCamelRecord),
    payrollRuleHistory: [],
    incentiveRuleHistory: incentiveRuleHistory.data.map(toCamelRecord),
    punches: punches.data.map(toCamelRecord),
    attendanceOverrides: corrections.data
      .map(toCamelRecord)
      .filter((item) => item.status === "approved" && item.requestedValue)
      .map((item) => ({ ...item.requestedValue, employeeId: item.employeeId, date: item.date, id: item.id })),
    corrections: corrections.data.map(toCamelRecord),
    leaveBalances: leaveBalances.data.map(toCamelRecord),
    advancesLoans: advancesLoans.data.map(toCamelRecord),
    reimbursements: reimbursements.data.map(toCamelRecord),
    storeCollections: storeCollections.data.map(toCamelRecord),
    incentiveRecords: incentiveRecords.data.map(toCamelRecord),
    payrollRuns: payrollRuns.data.map(toCamelRecord),
    salarySlips: [],
    approvals: [],
    workTasks: workTasks.data.map(toCamelRecord),
    salesTargets: salesTargets.data.map(toCamelRecord),
    opticalCustomers: opticalCustomers.data.map(toCamelRecord),
    opticalFastSlips: opticalFastSlips.data.map(toCamelRecord),
    opticalDailyRegisterEntries: opticalDailyRegisterEntries.data.map(toCamelRecord),
    opticalPendingPaymentEntries: opticalPendingPaymentEntries.data.map(toCamelRecord),
    opticalDeliveryTrackingEntries: opticalDeliveryTrackingEntries.data.map(toCamelRecord),
    opticalLensOrderEntries: opticalLensOrderEntries.data.map(toCamelRecord),
    crmProducts: crmProducts.data.map(toCamelRecord),
    crmStockMovements: crmStockMovements.data.map(toCamelRecord),
    crmPurchases: crmPurchases.data.map(toCamelRecord),
    crmSuppliers: crmSuppliers.data.map(toCamelRecord),
    crmProductCategories: crmProductCategories.data.map(toCamelRecord),
    crmBrands: crmBrands.data.map(toCamelRecord),
    crmGstHsnMaster: crmGstHsnMaster.data.map(toCamelRecord),
    crmInventoryStock: crmInventoryStock.data.map(toCamelRecord),
    crmStockTransfers: crmStockTransfers.data.map(toCamelRecord),
    crmStockAdjustments: crmStockAdjustments.data.map(toCamelRecord),
    crmVendorOrderSlips: crmVendorOrderSlips.data.map(toCamelRecord),
    crmVendorDcInward: crmVendorDcInward.data.map(toCamelRecord),
    crmPurchaseInvoices: crmPurchaseInvoices.data.map(toCamelRecord),
    crmVendorBillAudits: crmVendorBillAudits.data.map(toCamelRecord),
    crmDocumentExtractions: crmDocumentExtractions.data.map(toCamelRecord),
    crmUnmappedSoldItems: crmUnmappedSoldItems.data.map(toCamelRecord),
    crmInventoryImports: crmInventoryImports.data.map(toCamelRecord),
    crmPurchaseImports: crmPurchaseImports.data.map(toCamelRecord),
    crmCommunications: crmCommunications.data.map(toCamelRecord),
    crmPrescriptions: crmPrescriptions.data.map(toCamelRecord),
    crmAppointments: crmAppointments.data.map(toCamelRecord),
    bankReco: bankReco.data.map(toCamelRecord),
    cnRequests: cnRequests.data.map(toCamelRecord),
    importBatches: importBatches.data.map(toCamelRecord),
    notifications: notifications.data.map(toCamelRecord),
    companyLedgerEntries: companyLedgerEntries.data.map(toCamelRecord),
    cnDocumentExtractions: cnDocumentExtractions.data.map(toCamelRecord),
    appFiles: appFiles.data.map(toCamelRecord),
    stockItems: stockItems.data.map(toCamelRecord),
    auditSessions: auditSessions.data.map(toCamelRecord),
    auditSessionItems: auditSessionItems.data.map(toCamelRecord),
    auditPhotos: auditPhotos.data.map(toCamelRecord),
    auditReviews: auditReviews.data.map(toCamelRecord),
    auditSettings: auditSettings.data.map(toCamelRecord),
    auditActivityLogs: auditActivityLogs.data.map(toCamelRecord),
    auditLogs: auditLogs.data.map(toCamelRecord)
  };
}

export async function loadAppData() {
  if (!hasSupabaseConfig) return { state: readLocalState(), mode: "local" };
  const session = await supabase.auth.getSession();
  if (!session.data.session) {
    const initialState = { ...createInitialState(), currentUserId: "" };
    try {
      const appSettings = await supabase.from("app_settings").select("*").eq("id", "global").maybeSingle();
      if (appSettings.data?.settings) {
        initialState.settings = { ...DEFAULT_SETTINGS, ...appSettings.data.settings };
      }
    } catch {
      // Public branding can fall back to defaults before login.
    }
    return { state: initialState, mode: "supabase" };
  }
  return { state: await loadSupabaseData(), mode: "supabase" };
}

export function saveLocalState(state) {
  writeLocalState(state);
}

export function createAuditLog({ entityType, entityId, action, oldValue, newValue, reason, effectiveDate, user }) {
  return {
    id: uuid("audit"),
    entityType,
    entityId,
    action,
    oldValue,
    newValue,
    reason,
    effectiveDate,
    changedBy: user?.name || user?.email || "system",
    createdAt: new Date().toISOString()
  };
}

export async function uploadAttendancePhoto({ dataUrl, employeeId, storeId = "", uploadedBy = "", type }) {
  const payload = {
    dataUrl,
    module: "attendance",
    recordType: "attendance_punch",
    recordId: `${employeeId}-${type}-${Date.now()}`,
    storeId,
    staffId: employeeId,
    fileType: type === "out" ? "attendance_selfie_out" : "attendance_selfie_in",
    uploadedBy,
    maxWidth: 800,
    quality: 0.64,
    targetMaxBytes: 500 * 1024
  };
  try {
    const result = await uploadCompressedImage({ ...payload, bucket: DEFAULT_FILE_BUCKET });
    return result.storagePath || result.path || result.url;
  } catch (error) {
    const message = String(error?.message || "").toLowerCase();
    const shouldTryAttendanceBucket =
      message.includes("bucket") ||
      message.includes("storage") ||
      message.includes("not found") ||
      message.includes("row-level security") ||
      message.includes("permission") ||
      message.includes("policy");
    if (!shouldTryAttendanceBucket) {
      throw new Error(`Attendance photo could not be uploaded: ${error.message || "please try again."}`);
    }
    try {
      const fallback = await uploadCompressedImage({ ...payload, bucket: "attendance-photos" });
      return fallback.storagePath || fallback.path || fallback.url;
    } catch (fallbackError) {
      throw new Error(`Attendance photo could not be uploaded. Please check camera/internet and try again. ${fallbackError.message || ""}`);
    }
  }
}

export async function getAttendancePhotoUrl(path) {
  if (!path) return "";
  try {
    return await getFileUrl(path, { bucket: DEFAULT_FILE_BUCKET, expiresIn: 60 * 5 });
  } catch {
    if (!hasSupabaseConfig || String(path).startsWith("data:")) return path;
    const { data, error } = await supabase.storage.from("attendance-photos").createSignedUrl(path, 60 * 5);
    if (error) throw error;
    return data.signedUrl;
  }
}

export async function persistEntity(tableName, record) {
  if (!hasSupabaseConfig) return record;
  if (tableName === "cn_requests") return persistCnRequestCompat(record);
  const data = await upsertSchemaSafe(tableName, record);
  return toCamelRecord(data);
}

export async function fetchEntityById(tableName, id) {
  if (!hasSupabaseConfig || !id) return null;
  const { data, error } = await supabase.from(tableName).select("*").eq("id", id).single();
  if (error) throw error;
  return toCamelRecord(data);
}

export async function persistAuditLog(log) {
  if (!hasSupabaseConfig) return log;
  const { error } = await supabase.from("audit_logs").insert(toSnakeRecord(log));
  if (error) throw error;
  return log;
}

export async function deleteEntity(tableName, id) {
  if (!hasSupabaseConfig) return;
  const { error } = await supabase.from(tableName).delete().eq("id", id);
  if (error) throw error;
}

export async function createAuthAccount({ email, password }) {
  if (!hasSupabaseConfig) return { user: null };
  const authClient = createNoSessionSupabaseClient();
  const { data, error } = await authClient.auth.signUp({
    email,
    password,
    options: {
      data: {
        created_by_app: "staffpay_attendance"
      }
    }
  });

  if (error && !String(error.message).toLowerCase().includes("already registered")) {
    throw error;
  }

  return { user: data?.user || null, alreadyExists: Boolean(error) };
}

export { uuid, hasSupabaseConfig };
